import { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import type {
  FulfillmentEvent,
  PackingRecordProduct,
  PackingRecordRecording,
  PackingScanLogEntry,
} from '@/infrastructure/FulfillmentAdminClient';
import { kstClock, kstTimestamp } from '@/utils/fulfillmentTime';
import { BoxIcon } from './UltimateIcons';

export const PACKING_RECORDED_OPERATION = 'PACKING_RECORDED';

/** One PACKING_RECORDED event as Order Detail reads it (SSD-7587); fields the server did not send read as absent. */
export interface PackingRecordView {
  id: string;
  actor: string | null;
  occurredAt: string;
  startedAt: string | null;
  labelScannedAt: string | null;
  label: { code: string; matchesOrder: boolean; sharedWith: number[]; ownerOrderIds: number[] } | null;
  units: { expected: number; scanned: number } | null;
  products: PackingRecordProduct[];
  recording: PackingRecordRecording | null;
  scans: PackingScanLogEntry[];
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const asNumbers = (value: unknown): number[] =>
  Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : [];

/** The order's packing records, newest first; an event whose payload cannot be read is still listed, with blanks. */
export const packingRecordsOf = (events: readonly FulfillmentEvent[]): PackingRecordView[] =>
  events
    .filter((event) => event.operation === PACKING_RECORDED_OPERATION)
    .map((event) => {
      const payload = asRecord(event.payload) ?? {};
      const label = asRecord(payload['label']);
      const units = asRecord(payload['units']);
      const recording = asRecord(payload['recording']);
      return {
        id: event.id,
        actor: event.actor || null,
        occurredAt: event.occurredAt,
        startedAt: asString(payload['startedAt']),
        labelScannedAt: asString(payload['labelScannedAt']),
        label: label
          ? {
              code: asString(label['code']) ?? '',
              matchesOrder: label['matchesOrder'] === true,
              sharedWith: asNumbers(label['sharedWith']),
              ownerOrderIds: asNumbers(label['ownerOrderIds']),
            }
          : null,
        units: units ? { expected: asNumber(units['expected']) ?? 0, scanned: asNumber(units['scanned']) ?? 0 } : null,
        products: Array.isArray(payload['products']) ? (payload['products'] as PackingRecordProduct[]) : [],
        recording: recording ? (recording as unknown as PackingRecordRecording) : null,
        scans: Array.isArray(payload['scans']) ? (payload['scans'] as PackingScanLogEntry[]) : [],
      };
    })
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

/** What the two Zero Packing signals came to, in a few words; the video itself is in Packing Videos above. */
export const describeRecording = (recording: PackingRecordRecording | null): { text: string; tone: 'ok' | 'warn' } => {
  if (!recording) {
    return { text: '—', tone: 'warn' };
  }
  if (!recording.enabled) {
    return { text: 'Packed without Zero Packing', tone: 'warn' };
  }
  const started = recording.start === 'OK' || recording.start === 'CONFIRMED_BY_HAND';
  if (!started) {
    return { text: recording.start === 'NOT_SENT' ? 'Not started' : 'Start not confirmed', tone: 'warn' };
  }
  if (recording.stop === 'BY_HAND') {
    return { text: 'Stopped by hand before the label', tone: 'warn' };
  }
  if (recording.stop !== 'OK') {
    return { text: 'Stop not confirmed', tone: 'warn' };
  }
  // The app's 201 is a receipt, not a video: the video itself is the proof, in Packing Videos above.
  return {
    text: recording.start === 'CONFIRMED_BY_HAND' ? 'Start seen in the app, stop sent' : 'Start and stop sent',
    tone: 'ok',
  };
};

const tookOf = (startedAt: string | null, endedAt: string | null): string => {
  if (!startedAt || !endedAt) {
    return '—';
  }
  const seconds = Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '—';
  }
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
};

/**
 * Order Detail › Packing Records (SSD-7587): what the Packing desk kept when the carrier label was scanned off the box —
 * who packed it, the units counted, the label and how the Zero Packing recording went — with the products and every
 * scan folded under each record.
 */
export function PackingRecordsSection({ events }: { events: readonly FulfillmentEvent[] }) {
  const records = packingRecordsOf(events);
  const ref = useRef<HTMLDivElement | null>(null);
  // View Orders and Packing link here with #packing-records; the page loads after the router's own jump, so the
  // section brings itself into view once it is drawn.
  useEffect(() => {
    if (window.location.hash === '#packing-records') {
      if (typeof ref.current?.scrollIntoView === 'function') {
        ref.current.scrollIntoView({ block: 'start' });
      }
    }
  }, []);
  return (
    <div className="sec" id="packing-records" data-testid="packing-records" ref={ref}>
      <h3>
        <BoxIcon /> Packing Records{records.length > 0 ? ` (${records.length})` : ''}
      </h3>
      {records.length === 0 ? (
        <p className="blocked-note" style={{ margin: '4px 0 0' }}>
          No packing record yet. Fulfillment › Packing keeps one when the carrier label is scanned off the box.
        </p>
      ) : (
        <div className="trackscroll">
          <table className="packing-records-table" style={{ tableLayout: 'fixed', width: '100%', minWidth: 760 }}>
            <colgroup>
              <col style={{ width: 150 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 80 }} />
              <col />
              <col style={{ width: 220 }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Packed</th>
                <th>Packer</th>
                <th className="num">Units</th>
                <th>Carrier label</th>
                <th>Recording</th>
                <th className="num">Took</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                const recording = describeRecording(record.recording);
                const label = record.label;
                return (
                  <Fragment key={record.id}>
                    <tr data-testid="packing-record">
                      <td className="num">{kstTimestamp(record.labelScannedAt ?? record.occurredAt)}</td>
                      <td style={{ overflowWrap: 'anywhere' }}>{record.actor ?? '—'}</td>
                      <td className="num">
                        {record.units ? `${record.units.scanned} / ${record.units.expected}` : '—'}
                      </td>
                      <td style={{ overflowWrap: 'anywhere' }}>
                        <code>{label?.code || '—'}</code>{' '}
                        {label && !label.matchesOrder && (
                          <span className="node" style={{ color: '#b02a37' }}>
                            not this order&apos;s label
                            {label.ownerOrderIds.length > 0 ? ` (order ${label.ownerOrderIds.join(', ')})` : ''}
                          </span>
                        )}
                        {label && label.sharedWith.length > 0 && (
                          <span className="node">also ships {label.sharedWith.join(', ')}</span>
                        )}
                      </td>
                      <td style={recording.tone === 'warn' ? { color: '#9a6700' } : undefined}>{recording.text}</td>
                      <td className="num">{tookOf(record.startedAt, record.labelScannedAt)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6} style={{ paddingTop: 0 }}>
                        <details>
                          <summary className="blocked-note">
                            Products ({record.products.length}) and scans ({record.scans.length})
                          </summary>
                          <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                            {record.products.map((product) => (
                              <li key={product.key}>
                                {product.name ?? product.key} <code>{product.key}</code> · {product.scanned} /{' '}
                                {product.expected}
                                {product.byEye > 0 ? ` · ${product.byEye} by eye` : ''}
                              </li>
                            ))}
                          </ul>
                          <ol className="num" style={{ margin: '6px 0', paddingLeft: 22, fontSize: 12.5 }}>
                            {record.scans.map((scan, index) => (
                              <li key={`${scan.at}-${index}`}>
                                {kstClock(scan.at)} {scan.result}
                                {scan.code ? ` · ${scan.code}` : ''}
                                {scan.sku && scan.sku !== scan.code ? ` · ${scan.sku}` : ''}
                              </li>
                            ))}
                          </ol>
                        </details>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * View Orders › Packing Records (SSD-7587, DY 10/22): the desk opens an order on View Orders, not on Order Detail, so
 * the boxes Packing kept are listed under the Inbound / Outbound Log too. Nothing shows until the order has one; the
 * products and every scan stay on Order Detail, one link away.
 */
export function PackingRecordsLog({ events, orderId }: { events: readonly FulfillmentEvent[]; orderId: number }) {
  const records = packingRecordsOf(events);
  if (records.length === 0) {
    return null;
  }
  return (
    <div className="logsec" data-testid="packing-records-log">
      <h4>
        Packing Records ({records.length}){' '}
        <Link className="pr-link" to={`${ROUTES.fulfillment.orderDetail(orderId)}#packing-records`}>
          Products and scans on Order Detail
        </Link>
      </h4>
      <table className="logtbl" aria-label="Packing Records">
        <thead>
          <tr>
            <th>Packed</th>
            <th>Packer</th>
            <th>Units</th>
            <th>Carrier label</th>
            <th>Recording</th>
            <th>Took</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const recording = describeRecording(record.recording);
            return (
              <tr key={record.id} data-testid="packing-record-log">
                <td className="num">{kstTimestamp(record.labelScannedAt ?? record.occurredAt)}</td>
                <td>{record.actor ?? '—'}</td>
                <td className="num">{record.units ? `${record.units.scanned} / ${record.units.expected}` : '—'}</td>
                <td className="num">
                  {record.label?.code || '—'}
                  {record.label && !record.label.matchesOrder ? " · not this order's label" : ''}
                </td>
                <td className={recording.tone === 'warn' ? 'act-warn' : undefined}>{recording.text}</td>
                <td className="num">{tookOf(record.startedAt, record.labelScannedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
